import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { CircularProgress } from '@material-ui/core';
import { whatsappOficialApi } from 'api/whatsappOficialApi';
import { getTenantId } from 'util/isAdmin';
import { useNotifyAlert } from 'context/NotifyAlert';
import { useMessage } from '../../context/MessageContext';
import {
	SETTINGS_VIEW_HEALTH,
	TEMPLATE_PARAM_DATA_OPTIONS,
	TEMPLATE_REFERENCE_TYPES,
} from '../../utils/whatsappSIdebarTabs';
import { getReferenceLabel } from '../../utils/whatsappTemplates';
import * as S from './styles';

// Idioma enviado pelo backend; é a versão do template que é configurada.
const SEND_LANGUAGE = 'pt_BR';
const PLACEHOLDER_SPLIT = /(\{\{\s*[^{}\s]+\s*\}\})/g;
const IS_PLACEHOLDER = /^\{\{\s*[^{}\s]+\s*\}\}$/;

const resolveFormat = ( parameterFormat, placeholders ) => {
	const format = parameterFormat?.toUpperCase();
	if ( format === 'NAMED' || format === 'POSITIONAL' ) return format;
	return placeholders.every( p => /^\d+$/.test( p ) ) ? 'POSITIONAL' : 'NAMED';
};

// Mesma ordem do envio: posicionais pelo número, nominais na ordem do texto.
const orderPlaceholders = ( placeholders, format ) => ( format === 'POSITIONAL'
	? [ ...placeholders ].sort( ( a, b ) => Number( a ) - Number( b ) )
	: placeholders );

const TemplateBody = ( { text } ) => (
	<S.Bubble>
		{text.split( PLACEHOLDER_SPLIT ).map( ( part, idx ) => (
			IS_PLACEHOLDER.test( part )
				? <S.Placeholder key={idx}>{part}</S.Placeholder>
				: <React.Fragment key={idx}>{part}</React.Fragment>
		) )}
	</S.Bubble>
);

// O sync grava `reference = name` nos templates sem tipo, então essa igualdade é "sem mapeamento".
const referenceOf = ( reference, template ) => ( reference && reference !== template?.name ? reference : '' );

const TemplateConfig = () => {
	const { settingsTemplate: template, setSettingsView } = useMessage();
	const queryClient = useQueryClient();
	const { setShowNotify } = useNotifyAlert();

	const [ loading, setLoading ] = useState( true );
	const [ loadError, setLoadError ] = useState( '' );
	const [ meta, setMeta ] = useState( null );
	// já abre com o tipo da listagem, para não mostrar "sem mapeamento" num template mapeado
	const [ reference, setReference ] = useState( () => referenceOf( template?.reference, template ) );
	const [ params, setParams ] = useState( {} );
	const [ saving, setSaving ] = useState( false );

	const backToHealth = () => setSettingsView( SETTINGS_VIEW_HEALTH );

	useEffect( () => {
		if ( !template ) {
			backToHealth();
			return;
		}
		const load = async () => {
			setLoading( true );
			setLoadError( '' );
			try {
				const res = await whatsappOficialApi.getTemplateDetails( getTenantId(), template.name );
				const config = res.data?.config;
				setMeta( res.data?.templates?.find( t => t.language === SEND_LANGUAGE ) ?? null );
				const current = referenceOf( config?.reference ?? template.reference, template );
				const mapped = !!current && !!config;
				setReference( current );
				setParams( mapped
					? Object.fromEntries( ( config.parameters ?? [] ).map( p => [ p.placeholder, p.dataKey ] ) )
					: {} );
			} catch ( e ) {
				console.error( 'TemplateConfig.load error', e );
				setLoadError( 'Erro ao consultar o template na Meta. Tente novamente.' );
			} finally {
				setLoading( false );
			}
		};
		load();
	}, [] ); //eslint-disable-line

	const format = useMemo( () => resolveFormat( meta?.parameterFormat, meta?.variables?.body ?? [] ), [ meta ] );
	const placeholders = useMemo( () => orderPlaceholders( meta?.variables?.body ?? [], format ), [ meta, format ] );

	const unsupported = !meta
		? `Este template não tem versão ${SEND_LANGUAGE} na Meta e não pode ser enviado.`
		: ( meta.variables?.header?.length || meta.variables?.buttons?.length )
			? 'Este template tem parâmetros no cabeçalho ou em botões, que ainda não são suportados.'
			: '';
	const hasUnmappedParam = placeholders.some( p => !params[ p ] );
	// "Sem mapeamento" sempre pode ser salvo; com tipo, todo parâmetro precisa de dado.
	const canSave = !saving && ( !reference || ( !unsupported && !hasUnmappedParam ) );

	const handleSave = async () => {
		setSaving( true );
		try {
			await whatsappOficialApi.saveTemplateConfig( template.id, {
				reference,
				parameters: reference ? placeholders.map( placeholder => ( { placeholder, dataKey: params[ placeholder ] } ) ) : [],
			} );
			// o modal de envio usa esta lista para saber quais templates têm tipo
			queryClient.invalidateQueries( [ 'whatsappOficialTemplates' ] );
			setShowNotify( {
				status : 'success',
				text   : 'Configuração do template salva com sucesso!'
			} );
			backToHealth();
		} catch ( e ) {
			console.error( 'TemplateConfig.save error', e );
			const { status, data } = e?.response ?? {};
			// 409: o tipo já está em outro template do cliente — diz qual e como liberar.
			// Os demais erros do backend (template sem pt_BR…) já vêm com a mensagem pronta.
			setShowNotify( status === 409 && data?.templateName
				? {
					status  : 'error',
					title   : 'Tipo no sistema já em uso',
					text    : `"${getReferenceLabel( reference )}" já está no template ${data.templateName}. Para usar aqui, abra o ${data.templateName} e mude o tipo dele para "— sem mapeamento —".`,
					timeOut : 10000
				}
				: {
					status : 'error',
					title  : 'Não foi possível salvar',
					text   : data?.error ?? 'Erro ao salvar configuração.'
				} );
			setSaving( false );
		}
	};

	if ( !template ) return null;

	// mesmo padrão das demais telas de Configurações (ex.: MetaApiConfig): nada da
	// configuração aparece até a Meta responder, para não exibir tipo/parâmetros provisórios
	if ( loading ) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: '40px', width: '100%' }}>
				<CircularProgress size={32} style={{ color: '#008069' }} />
			</div>
		);
	}

	return (
		<S.Container>
			<S.Card $row>
				<S.CardInfo>
					<S.Hint>Template sincronizado da Meta</S.Hint>
					<S.TemplateName>{template.name}</S.TemplateName>
					<S.Chips>
						{( meta?.category ?? template.category ) && <S.Chip>{meta?.category ?? template.category}</S.Chip>}
						{( meta?.status ?? template.status ) && <S.Chip>{meta?.status ?? template.status}</S.Chip>}
						{meta && placeholders.length > 0 && (
							<S.Chip>Formato dos parâmetros: {format === 'NAMED' ? 'nominal' : 'posicional'}</S.Chip>
						)}
					</S.Chips>
				</S.CardInfo>
				<S.Field>
					<S.Label htmlFor="template-reference">Tipo no sistema</S.Label>
					<S.Select
						id="template-reference"
						value={reference}
						onChange={e => setReference( e.target.value )}
						disabled={!!loadError}
					>
						<option value="">— sem mapeamento —</option>
						{TEMPLATE_REFERENCE_TYPES.map( type => (
							<option key={type.value} value={type.value}>{type.label}</option>
						) )}
					</S.Select>
					<S.Hint>Define em qual etapa do workflow este template é enviado.</S.Hint>
				</S.Field>
			</S.Card>

			{loadError && <S.StatusText>{loadError}</S.StatusText>}

			{!loadError && (
				<>
					{unsupported && <S.Warning>{unsupported}</S.Warning>}

					{meta && (
						<S.Card>
							<S.CardHeader>
								<S.SectionTitle>Texto do template</S.SectionTitle>
								<S.Hint>Somente leitura — edição é feita na Meta</S.Hint>
							</S.CardHeader>
							<S.ChatBackground>
								<TemplateBody text={meta.bodyText ?? ''} />
							</S.ChatBackground>
						</S.Card>
					)}

					{meta && placeholders.length > 0 && (
						<S.TableWrapper>
							<S.TableIntro>
								<S.SectionTitle>Parâmetros do template</S.SectionTitle>
								<S.Hint>Escolha qual dado da plataforma preenche cada parâmetro no momento do envio.</S.Hint>
							</S.TableIntro>
							<S.TableHeader>
								<span>Ordem</span>
								<span>Parâmetro na Meta</span>
								<span>Dado da plataforma</span>
							</S.TableHeader>
							{placeholders.map( ( placeholder, idx ) => {
								const missing = !!reference && !params[ placeholder ];
								return (
									<S.TableRow key={placeholder} $missing={missing}>
										<S.Order>{idx + 1}</S.Order>
										<S.ParamName>{`{{${placeholder}}}`}</S.ParamName>
										<S.ParamField>
											<S.Select
												aria-label={`Dado para ${placeholder}`}
												value={params[ placeholder ] ?? ''}
												onChange={e => {
													// React 16 reaproveita o evento: ler o valor antes do updater
													const { value } = e.target;
													setParams( prev => ( { ...prev, [ placeholder ]: value } ) );
												}}
												$missing={missing}
											>
												<option value="">Selecione um dado</option>
												{TEMPLATE_PARAM_DATA_OPTIONS.map( option => (
													<option key={option.value} value={option.value}>{option.label}</option>
												) )}
											</S.Select>
											{missing && <S.MissingText>Parâmetro sem dado vinculado</S.MissingText>}
										</S.ParamField>
									</S.TableRow>
								);
							} )}
						</S.TableWrapper>
					)}
				</>
			)}

			<S.Actions>
				<S.CancelButton type="button" onClick={backToHealth}>Cancelar</S.CancelButton>
				<S.SaveButton type="button" onClick={handleSave} disabled={!canSave || !!loadError}>
					{saving ? 'Salvando...' : 'Salvar configuração'}
				</S.SaveButton>
			</S.Actions>
		</S.Container>
	);
};

export default TemplateConfig;
